import { Component, EventEmitter, OnInit, Output, inject, DestroyRef } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { translate } from '@jsverse/transloco';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { combineLatest, filter, finalize, map, Observable, take } from 'rxjs';
import { ConfirmationDialogComponent } from 'src/app/components/confirmation-dialog/confirmation-dialog.component';
import { ErrorDialogComponent } from 'src/app/components/error-dialog/error-dialog.component';
import { GenericErrorComponent } from 'src/app/components/generic-error/generic-error.component';
import { BreadcrumbItem } from 'src/app/models/breadcrumb/breadcrumb-item';
import { DeployStatus } from 'src/app/models/deploy/deploy-status';
import { DeploymentClass } from 'src/app/models/instance-class';
import { Permission } from 'src/app/models/principal/permission';
import { RouterSegments } from 'src/app/models/router-segments';
import { Router } from '@angular/router';
import { SolutionVersionsInfo } from 'src/app/models/service/solution-versions-info';
import { Tenant } from 'src/app/models/tenant/tenant';
import { BlueprintsService } from 'src/app/services/api/blueprints.service';
import { InstancesService } from 'src/app/services/api/instances.service';
import { SolutionsService } from 'src/app/services/api/solutions.service';
import { TenantService } from 'src/app/services/api/tenant.service';
import { AuthService } from 'src/app/services/auth/auth.service';
import { ErrorPageService } from 'src/app/services/util/error-page.service';
import { PermissionsService } from 'src/app/services/util/permissions.service';
import { SidenavService } from 'src/app/services/util/sidenav.service';
import { SolutionVersionsService } from 'src/app/services/util/solution-versions.service';
import { CustomToastrService } from 'src/app/services/util/toastr.service';
import { MatDialog } from '@angular/material/dialog';
import { Permissions, Datazone, Attributes } from 'src/app/models/principal/permission';
import { PermissionKeys } from 'src/app/models/principal/permission-keys';
import { TenantOrigin } from 'src/app/models/tenant/tenant-status';
import { DisasterRecoveryService } from 'src/app/services/api/disaster-recovery.service'

const REQUIRED_PERMISSIONS = [
  Permission.DR_ACTION,
  {
    key: PermissionKeys.INSTANCE_DELETE_DATAZONE_NONPII,
    action: Permissions.INSTANCE_DELETE,
    attribute: Attributes.DATAZONE,
    attributeValue: Datazone.NONPII
  },
  {
    key: PermissionKeys.INSTANCE_DELETE_DATAZONE_PII,
    action: Permissions.INSTANCE_DELETE,
    attribute: Attributes.DATAZONE,
    attributeValue: Datazone.PII
  },
  {
    key: PermissionKeys.INSTANCE_DELETE_DATAZONE_LIVE,
    action: Permissions.INSTANCE_DELETE,
    attribute: Attributes.DATAZONE,
    attributeValue: Datazone.LIVE
  },
  {
    key: PermissionKeys.BLUEPRINT_CREATE,
    action: Permissions.BLUEPRINT_CREATE,
  },
  {
    key: PermissionKeys.BLUEPRINT_DELETE,
    action: Permissions.BLUEPRINT_DELETE,
  },
  {
    key: PermissionKeys.BLUEPRINT_READ,
    action: Permissions.BLUEPRINT_READ,
  },
  {
    key: PermissionKeys.BLUEPRINT_UPDATE,
    action: Permissions.BLUEPRINT_UPDATE,
  },
  {
    key: PermissionKeys.INSTANCE_DR_DATAZONE_LIVE,
    action: Permissions.INSTANCE_DR,
    attribute: Attributes.DATAZONE,
    attributeValue: Datazone.LIVE
  }
];
@Component({
    selector: 'app-blueprint-summary',
    templateUrl: './blueprint-summary.component.html',
    styleUrls: ['./blueprint-summary.component.scss'],
    standalone: false
})
export class BlueprintSummaryComponent  implements OnInit {
  blueprintId: string;
  instanceId: string;
  blueprint: any;
  viewReleaseNotes: string;
  solution: any;
  solutionVersions: any[];
  instances: any[];
  serviceVersionsInfo: SolutionVersionsInfo;
  latestReleaseVersion: any;
  isAMR: boolean;
  tenantData: Tenant;
  latestServiceDefinitionVersion: any;
  isLive: boolean;
  selectedMenu: any;
  RouterSegments = RouterSegments;
  tenantId: any;
  sortedSolutionVersionId: any;
private destroyRef = inject(DestroyRef);
  readonly INSTANCE_DEPLOYED_STATES = [DeployStatus.DEPLOYED, DeployStatus.CONFIGURING,
    DeployStatus.CONFIGURATION_FAILED, DeployStatus.CONFIGURED, DeployStatus.UPDATING, DeployStatus.UPDATED, DeployStatus.TESTED,
    DeployStatus.UPDATE_FAILED, DeployStatus.TEST_FAILED];

  breadcrumbItems: BreadcrumbItem[] = [];
  verticalMenuItems: any;
  permissionsMap: { [key: string]: boolean };
  allInstances: any;
  isLoading: boolean;
  tenant: any;
  service: any;
  Permissions = Permissions;
  PermissionKeys = PermissionKeys;
  isExternalTenant = false;
  drInProgress$: Observable<boolean>;
  isDREnabled$: Observable<boolean>;
  showDRBanner$: Observable<boolean>;

  constructor(
    private router: Router,
    private activatedRoute: ActivatedRoute,
    private blueprintsService: BlueprintsService,
    private solutionsService: SolutionsService,
    private instanceService: InstancesService,
    private solutionVersionsService: SolutionVersionsService,
    private toastrService: CustomToastrService,
    private tenantService: TenantService,
    private errorPageService: ErrorPageService,
    private sidenavService: SidenavService,
    public authService: AuthService,
    public permissionsService: PermissionsService, public matDialog: MatDialog, private disasterRecoveryService: DisasterRecoveryService) {
      this.drInProgress$ = this.disasterRecoveryService.drInProgress$;
      this.isDREnabled$ = this.disasterRecoveryService.isDREnabled$;
    
  }

  ngOnInit(): void {
    this.showDRBanner$ = combineLatest([this.drInProgress$, this.isDREnabled$]).pipe(
      map(([drInProgress, isDREnabled]) => drInProgress === true && isDREnabled === true)
    );
    localStorage.removeItem('selectedVersionExtend');
    this.permissionsMap = this.permissionsService.resolveAPIPermissions(REQUIRED_PERMISSIONS);
    this.sidenavService.tenantSelectedMenu = 'TENANT_LAYOUT.MENU.SERVICE_DEFINITION';
    this.tenantId = this.activatedRoute.snapshot.params.id;
    if (!this.permissionsMap[PermissionKeys.BLUEPRINT_READ]) {
      this.router.navigate(['/', this.tenantId, 'tenant', 'permission-unauthorized'], {skipLocationChange: true});
    }
    this.verticalMenuItems = [
      {
        nameKey: '',
        items: [
          {
            nameKey: 'BLUEPRINT_SUMMARY.INSTANCES',
            icon: 'language',
            expanded: false,
          },
        ]
      },
      {
        nameKey: 'BLUEPRINT_SUMMARY.SERVICE_DEFINITION',
        items: [
          {
            nameKey: 'BLUEPRINT_SUMMARY.SETTINGS',
            icon: 'settings',
            expanded: false,
          },
          {
            nameKey: 'BLUEPRINT_SUMMARY.SERVICE_DEF_VERSIONS',
            icon: 'pages',
            expanded: false,
          }
        ]
      },
      {
        nameKey: 'BLUEPRINT_SUMMARY.LIVE',
        items: [
          {
            nameKey: 'BLUEPRINT_SUMMARY.LIVE_METRICS',
            icon: 'show_chart',
            expanded: false,
          },
          {
            nameKey: 'BLUEPRINT_SUMMARY.USAGE_COST',
            icon: 'bar_chart',
            expanded: false,
          }
        ]
      },
      {
        nameKey: 'BLUEPRINT_SUMMARY.RECOVERY_MANAGEMENT',
        items: [
          {
            nameKey: 'BLUEPRINT_SUMMARY.DISASTER_RECOVERY',
            icon: 'emergency',
            expanded: false,
            disabled: true
          }
        ]
      },
      {
        nameKey: 'BLUEPRINT_SUMMARY.EVENTS',
        items: [
          {
            nameKey: 'BLUEPRINT_SUMMARY.ALERTS',
            icon: 'notifications',
            expanded: false,
          },
          {
            nameKey: 'BLUEPRINT_SUMMARY.LOGS',
            icon: 'list',
            expanded: false,
          }
        ]
      },
    ];
    if (!this.permissionsMap[Permission.DR_ACTION]) {
      this.verticalMenuItems = this.verticalMenuItems.filter(item => item?.nameKey !== 'BLUEPRINT_SUMMARY.RECOVERY_MANAGEMENT');
    }
    this.selectedMenu = this.verticalMenuItems[0].items[0];
    this.blueprintId = this.activatedRoute.snapshot.params.blueprintId;
    this.getTenantDetails();
    this.loadBlueprint();
    this.tenantService.tenant$.pipe(filter(tenant => tenant != null),take(1),takeUntilDestroyed(this.destroyRef)).subscribe(tenant => {
    this.isExternalTenant = tenant?.origin === TenantOrigin.EXTERNAL? true : false;
    });
  }

  deleteBp(data): void {
    this.allInstances = data;
  }

  getTenantDetails() {
    this.tenantId = this.activatedRoute.snapshot.params.id;
    this.breadcrumbItems.push({icon: 'assets/images/home_icon.svg', url:[RouterSegments.TENANT, this.tenantId] });
    this.breadcrumbItems.push({label: translate('TENANT_LAYOUT.MENU.SERVICE_DEFINITION'), url:[RouterSegments.TENANT, this.tenantId, RouterSegments.SERVICE_DEFINITION]});
    this.tenantService.getTenantDetails(this.tenantId)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(data => {
        this.tenantData = data;
      });
  }

  deleteBlueprint(): void {
    if (this.allInstances?.length > 0) {
      this.matDialog.open(GenericErrorComponent, {
        minHeight: '342px',
        width: '566px',
        panelClass: 'flex-dialog'
      });
    } else {
      this.matDialog.open(ConfirmationDialogComponent, {
        panelClass: 'default-dialog-wrapper',
        width: '566px',
        autoFocus: false,
        data: {
          message: translate('BLUEPRINT_SUMMARY.CONFIRM_DELETE_BLUEPRINT')
        }
      })
      .afterClosed()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(confirm => {
        if (confirm && this.allInstances?.length === 0) {
          this.isLoading = true;
          this.blueprintsService.deleteBlueprint(this.blueprint.id)
          .pipe(
            finalize(() => this.isLoading = false),
            takeUntilDestroyed(this.destroyRef))
          .subscribe(() => {
            this.router.navigate(['/',this.authService.principal.provider, 'tenant', this.tenantId, RouterSegments.BLUEPRINTS]);
          },
          (error) => {
            const errorMessage = typeof error === 'string' ? error: typeof error?.error === 'string' ? error.error : error;
            this.matDialog.open(ErrorDialogComponent, {
              minHeight: '342px',
              width: '566px',
              panelClass: 'flex-dialog',
              data: {
                title:  translate('NOTIFICATIONS.DELETE_SERVICE_DEFINITION_FAILED'),
                description: errorMessage,
              }
            });
          });
        }
      });
    }
  }

  private loadBlueprint(): void {
    this.blueprintsService.getBlueprints()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(blueprints => {
        this.blueprint = blueprints.find(item => item.id === this.blueprintId);
        this.verticalMenuItems[3].items[0].disabled = false;
        this.breadcrumbItems.push({label: this.blueprint?.name});
        this.loadService();
      }, (error) => {
        this.errorPageService.show404Page()
      });
  }

  private loadService(): void {
    const solutionId = this.blueprint?.solutionId;
    combineLatest([this.solutionsService.getSolutionById(solutionId), this.solutionsService.getSolutionVersions(solutionId)])
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(([solution, solutionVersions]) => {
        this.solution = solution;
        this.solutionVersions = solutionVersions;
        this.sortedSolutionVersionId =
          (solutionVersions.sort((a,b) => a?.version && b?.version ? b?.version?.localeCompare(a?.version) : !b?.version ? -1 : 1));
        this.viewReleaseNotes = this.solutionVersions[this.solutionVersions.length - 1]?.releaseNotes;
        this.loadInstances();
      }, (error) => {
        setTimeout(() => {
		  const title = translate('ERRORS.SERVICE_AVAILABILITY');
          const message = error?.message ? error?.message : (error?.error?.message ? error?.error?.message : (error?.errmsg ? error?.errmsg : translate('SERVICE.INTERNAL_SERVER_ERROR')));
          this.toastrService.errMessage(message, title);
        }, 500);
      });
  }

  private loadInstances(): void {
    const obj = {
      blueprintId: this.blueprintId,
      tenantId: this.tenantId
    };
    this.instanceService.getInstances(obj)
      .pipe(takeUntilDestroyed(this.destroyRef)).subscribe(
      (instances: any) => {
      this.instances = instances.data;
      this.isLive = this.resolveLiveStatus(this.instances);
      if (this.solution && this.solutionVersions) {
        this.serviceVersionsInfo = this.solutionVersionsService.getSolutionVersionsInfo(this.solution, this.solutionVersions, this.instances);
        const liveInstance = this.instances.find(instance => instance.deploymentClass === DeploymentClass.A);
        if (liveInstance) {
          this.latestReleaseVersion = this.solutionVersions.filter(version =>
            version.id === liveInstance.solutionVersionId)[0]?.releaseVersion;
        } else {
          const instanceLength = this.instances.length;
          const latestInstanceSolutionVersion = this.instances[instanceLength - 1]?.solutionVersionId;
          this.latestReleaseVersion = this.solutionVersions.filter(version =>
            version.id === latestInstanceSolutionVersion)[0]?.releaseVersion;
        }
        let releaseVersionText;
        if (this.latestReleaseVersion) {
          releaseVersionText = this.latestReleaseVersion;
        } else {
          releaseVersionText = this.serviceVersionsInfo?.currentVersion?.releaseVersion;
        }
        this.isAMR = releaseVersionText && this.solutionVersionsService.releaseVersion(releaseVersionText) ? true : false;
      }
    }, (error) => {
        setTimeout(() => {
		  const title = translate('ERRORS.SERVICE_AVAILABILITY');
          const message = error?.message ? error?.message : (error?.error?.message ? error?.error?.message : (error?.errmsg ? error?.errmsg : translate('SERVICE.INTERNAL_SERVER_ERROR')));
          this.toastrService.errMessage(message, title);
        }, 500);
    });
  }

  private resolveLiveStatus(instances: any[]): boolean {
    const live = instances?.find(instance => instance.deploymentClass === DeploymentClass.A);
    return this.INSTANCE_DEPLOYED_STATES.includes(live?.status);
  }

  public menuItemClicked(item) {
    this.selectedMenu = item;
  }
}
